<template>
    <div class="v-select" :class="{ 'disabled': disabled }">
        <p v-if="label" class="label">
            {{ label }}
            <span v-if="labelHint" class="label-hint">
                <simple-svg :src="InfoIcon" custom-class-name="icon" width="14" height="14" />
                <span class="hint-tooltip">{{ labelHint }}</span>
            </span>
        </p>
        <div class="select-wrapper" ref="selectWrapper" :class="{ 'open': isOpen, 'error': isError }"
            @click="toggleDropdown" :id="componentId" :style="{ 'min-width': minWidth, 'max-width': maxWidth }">
            <div class="selected-value">
                <span v-if="modelValue" class="text">{{ getLabel(modelValue) }}</span>
                <span v-else class="placeholder">{{ placeholder }}</span>
            </div>
            <simple-svg :src="ChevronIcon" :custom-class-name="'icon chevron-icon'" />

            <teleport to="body">
                <div class="v-select-options-list" v-if="isOpen" :style="dropdownStyle" :data-select-id="componentId">
                    <div class="option-item" v-for="option in options" :key="option.value"
                        :class="{ 'selected': option.value === modelValue }" @click.stop="selectOption(option)">
                        <span>{{ option.label }}</span>
                    </div>
                    <div v-if="options.length === 0" class="option-item no-options">
                        Нет вариантов
                    </div>
                </div>
            </teleport>
        </div>
    </div>
</template>

<script>
import ChevronIcon from '@/assets/icons/Caret_Down_MD.svg';
import InfoIcon from '@/assets/icons/Circle_Warning.svg';

export default {
    name: 'VSelect',
    props: {
        modelValue: {
            type: [String, Number, Object],
            default: null
        },
        options: {
            type: Array,
            required: true,
        },
        label: {
            type: String,
            default: ''
        },
        placeholder: {
            type: String,
            default: 'Выберите...'
        },
        minWidth: {
            type: String,
            default: '200px'
        },
        maxWidth: {
            type: String,
            default: '250px'
        },
        isError: {
            type: Boolean,
            default: false
        },
        disabled: {
            type: Boolean,
            default: false
        },
        labelHint: {
            type: String,
            default: ''
        }
    },
    emits: ['update:modelValue', 'change'],
    data() {
        return {
            isOpen: false,
            ChevronIcon,
            InfoIcon,
            dropdownStyle: {},
            componentId: `v-select-${Math.random().toString(36).substr(2, 9)}`,
            resizeObserver: null
        }
    },
    watch: {
        isOpen(val) {
            if (val) {
                this.updatePosition();
                window.addEventListener('scroll', this.handleScroll, true);
                window.addEventListener('resize', this.closeDropdown);
            } else {
                window.removeEventListener('scroll', this.handleScroll, true);
                window.removeEventListener('resize', this.closeDropdown);
            }
        }
    },
    mounted() {
        document.addEventListener('click', this.handleClickOutside);
    },
    beforeUnmount() {
        document.removeEventListener('click', this.handleClickOutside);
        window.removeEventListener('scroll', this.handleScroll, true);
        window.removeEventListener('resize', this.closeDropdown);
    },
    methods: {
        toggleDropdown() {
            if (!this.disabled) {
                this.isOpen = !this.isOpen;
            }
        },
        closeDropdown() {
            this.isOpen = false;
        },
        handleScroll(event) {
            // Close if scrolling happens outside the dropdown
            const dropdown = document.querySelector(`[data-select-id="${this.componentId}"]`);
            if (dropdown && !dropdown.contains(event.target)) {
                this.closeDropdown?.();
            }
        },
        handleClickOutside(event) {
            // Check if click is outside both wrapper and dropdown
            const wrapper = this.$refs.selectWrapper;
            const dropdown = document.querySelector(`[data-select-id="${this.componentId}"]`);

            if (wrapper && wrapper.contains(event.target)) return;
            if (dropdown && dropdown.contains(event.target)) return;

            this.closeDropdown();
        },
        selectOption(option) {
            this.$emit('update:modelValue', option.value);
            this.$emit('change', option);
            this.closeDropdown();
        },
        getLabel(value) {
            const option = this.options.find(opt => opt.value === value);
            return option ? option.label : value;
        },
        updatePosition() {
            if (!this.$refs.selectWrapper) return;

            const rect = this.$refs.selectWrapper.getBoundingClientRect();
            const minW = parseFloat(this.minWidth) || 200;
            const width = Math.max(rect.width, minW);
            this.dropdownStyle = {
                top: `${rect.bottom + 4}px`,
                left: `${rect.left}px`,
                width: `${width}px`,
                position: 'fixed'
            };
        }
    }
}
</script>

<style lang="scss">
.v-select {
    .label {
        font-weight: 600;
        font-size: 14px;
        margin-bottom: 5px;
        color: var(--color-text);
        display: flex;
        align-items: center;
        gap: 4px;

        .label-hint {
            position: relative;
            display: inline-flex;
            align-items: center;
            cursor: help;

            .icon {
                opacity: 0.6;
            }

            .hint-tooltip {
                display: none;
                position: absolute;
                bottom: calc(100% + 6px);
                left: 50%;
                transform: translateX(-50%);
                background-color: var(--color-bg-dark);
                border: 1px solid var(--color-border);
                color: var(--color-text);
                font-size: 12px;
                font-weight: 400;
                padding: 6px 10px;
                border-radius: 6px;
                white-space: normal;
                width: 220px;
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
                z-index: 100;
                line-height: 1.4;
            }

            &:hover .hint-tooltip {
                display: block;
            }
        }
    }

    &.disabled {
        opacity: 0.6;
        pointer-events: none;
    }
}

.select-wrapper {
    position: relative;
    box-sizing: border-box;
    background-color: var(--color-input-bg);
    border: solid 1px var(--color-border);
    border-radius: 5px;
    padding: 6px 8px;
    color: var(--color-input-text);
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 29px;

    &:hover {
        border-color: var(--color-input-focus);
    }

    &.open {
        border: solid 1px var(--color-input-focus);
        box-shadow: 0 0 6px var(--color-input-focus);

        .chevron-icon {
            transform: rotate(90deg);
        }
    }

    &.error {
        box-shadow: 0 0 6px var(--color-input-error);
    }

    .selected-value {
        flex: 1;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;

        .placeholder {
            color: var(--color-text-light);
        }
    }

    .chevron-icon {
        width: 16px;
        height: 16px;
        transition: transform 0.2s ease;
    }
}

/* Global scope styles for teleported content */
.v-select-options-list {
    position: fixed;
    /* Changed from absolute to fixed */
    background-color: var(--color-bg);
    border: 1px solid var(--color-border);
    border-radius: 6px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    z-index: 10005;
    /* Higher than popup */
    max-height: 200px;
    overflow-y: auto;
    padding: 4px;

    .option-item {
        padding: 6px 16px;
        border-radius: 4px;
        cursor: pointer;
        transition: background-color 0.2s;
        color: var(--color-text);

        &:hover {
            background-color: var(--color-bg-hover, rgba(0, 0, 0, 0.05));
        }

        &.selected {
            background-color: color-mix(in srgb, var(--color-blue) 10%, transparent);
            color: var(--color-blue);
            font-weight: 500;
        }

        &.no-options {
            color: var(--color-text-light);
            cursor: default;
            text-align: center;

            &:hover {
                background-color: transparent;
            }
        }
    }
}
</style>
